import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  StrictMode,
  useEffect,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Apple,
  CalendarDays,
  CarFront,
  Check,
  ChevronLeft,
  CirclePlay,
  CloudSun,
  BriefcaseBusiness,
  Clock3,
  EyeOff,
  Globe2,
  GripVertical,
  ListTodo,
  LogIn,
  Moon,
  MapPin,
  Pause,
  Play,
  Plus,
  Settings2,
  SkipForward,
  SkipBack,
  Sun,
  TimerReset,
  Trash2,
  Video,
  X,
  Languages,
} from "lucide-react";
import { io } from "socket.io-client";
import {
  initializeNativeApp,
  clearNativeSession,
  isNative,
  nativeFetch,
  nativeToken,
  openNativeAppleLogin,
  openNativeGoogleLogin,
  updateNativeWidget,
} from "./native";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AttentionCameraPanel,
  type AttentionPanelMode,
} from "@/components/attention/attention-camera-panel";
import type { AttentionSample } from "@/components/attention/attention-engine";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import "./styles.css";

type Theme = "LIGHT" | "DARK" | "SYSTEM";
type Language = "he" | "en";
type CountdownMode = "NEXT_EVENT" | "END_OF_DAY" | "CUSTOM_TIME" | "TIMER";
type User = {
  id: string;
  email: string;
  name?: string | null;
  locale: "HE" | "EN";
  theme: Theme;
  showSeconds: boolean;
  showGregorianDate: boolean;
  showWeather: boolean;
  showHebrewDate: boolean;
  showWeatherHighLow: boolean;
  showSpotifyPlayer: boolean;
  showGreeting: boolean;
  showCalendarAgenda: boolean;
  showAttentionCamera: boolean;
  attentionSyncEnabled: boolean;
  countdownMode: CountdownMode;
  countdownTargetTime: string | null;
  countdownDurationMinutes: number;
  countdownEndsAt: string | null;
  countdownTicking: boolean;
  countdownInWords: boolean;
  travelMode: "DRIVE" | "TRANSIT";
  travelOrigin: string;
  travelLeadMinutes: number;
  travelCalendarId: string | null;
  weatherLocation: string;
  defaultTaskEstimateMinutes: number;
};
type SpotifyState = {
  configured: boolean;
  connected: boolean;
  status?:
    | "DISCONNECTED"
    | "RECONNECT_REQUIRED"
    | "NO_ACTIVE_PLAYBACK"
    | "PLAYING";
  playback: {
    item?: {
      name?: string;
      artists?: Array<{ name?: string }>;
      album?: { images?: Array<{ url: string }> };
    };
    is_playing?: boolean;
    device?: { id?: string; name?: string };
  } | null;
  devices: Array<{ id: string; name: string; is_active: boolean }>;
};
type Project = {
  id: string;
  name: string;
  color?: string;
  currency?: string;
  client?: { id: string; name: string } | null;
};
type Task = {
  id: string;
  title: string;
  status: "INBOX" | "TODO" | "IN_PROGRESS" | "DONE" | "ARCHIVED";
  priority: string;
  estimateMinutes: number;
  sortOrder: number;
  dueAt: string | null;
  projectId: string | null;
  project?: Project | null;
};
type CalendarEvent = {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location?: string | null;
  conferenceUrl?: string | null;
  calendar: { summary: string; color: string | null };
};
type Focus = {
  currentEvent: CalendarEvent | null;
  nextEvent: CalendarEvent | null;
  activeSession: {
    id: string;
    startedAt: string;
    note?: string | null;
    task?: Task | null;
  } | null;
  todayEvents: CalendarEvent[];
  tomorrowEvents: CalendarEvent[];
  todayTotal: number;
  todayRemaining: number;
};
type Calendar = {
  id: string;
  summary: string;
  color: string | null;
  selected: boolean;
};
type CalendarState = {
  connected: boolean;
  lastSyncedAt: string | null;
  calendars: Calendar[];
  hiddenEvents: CalendarEvent[];
};
type TravelPlan = {
  eventId: string;
  mode: "DRIVE" | "TRANSIT";
  origin: string;
  destination: string;
  durationSeconds: number;
  distanceMeters: number | null;
  leadMinutes: number;
  recommendedDepartureAt: string;
};
type Weather = {
  location: string;
  temperature: number | null;
  temperatureMax: number | null;
  temperatureMin: number | null;
  weatherCode: number | null;
  isDay: boolean;
};
type AttentionDaily = {
  focusedSeconds: number;
  awaySeconds: number;
  unknownSeconds: number;
  totalSeconds: number;
  averageFocusScore: number;
};
type Copy = typeof messages.he | typeof messages.en;

const messages = {
  he: {
    tagline: "פחות רעש. יותר מיקוד.",
    signIn: "כניסה ל‑Beta",
    calendar: "יומנים",
    calendarCopy:
      "חבר/י את Google Calendar, ולאחר מכן בחר/י אילו יומנים יוצגו כאן.",
    tasks: "משימות",
    addTask: "משימה חדשה",
    focus: "המוקד שלך",
    noEvent: "יש לך מרחב להתמקד",
    noEventCopy: "אין אירוע קרוב. בחר/י משימה אחת והתחל/י בקצב שלך.",
    start: "התחל מיקוד",
    stop: "סיום מיקוד",
    today: "היום",
    active: "עובדים עכשיו",
    clock: "הזמן שלך",
    settings: "הגדרות",
    loading: "טוענים את המרחב שלך…",
    inbox: "תיבת כניסה",
    completed: "הושלם",
    loginTitle: "כל היום שלך, ברגע אחד.",
    loginCopy: "יומן, משימות ומדידת זמן במסך רגוע אחד.",
    google: "המשך עם Google",
    apple: "המשך עם Apple",
    magic: "קישור כניסה בדוא״ל",
    email: "כתובת דוא״ל",
    send: "שלחו לי קישור",
    invite: "הגישה ל‑Beta היא בהזמנה בלבד.",
    appearance: "מראה",
    system: "אוטומטי",
    light: "בהיר",
    dark: "כהה",
    calendarSettings: "בחירת יומנים",
    calendarSettingsCopy: "בחר/י רק את היומנים שרוצים לראות במרחב העבודה.",
    connectCalendar: "חיבור Google Calendar",
    connected: "מחובר",
    saveError: "לא הצלחנו לשמור את השינוי. אפשר לנסות שוב.",
    taskCreateError: "לא הצלחנו להוסיף את המשימה. אפשר לנסות שוב.",
    deleteTask: "מחיקת משימה",
    deleteTaskConfirm: "למחוק את המשימה? מדידת הזמן שכבר נשמרה תישאר זמינה.",
    accessibility: "הצהרת נגישות",
    selectedCalendars: "יומנים נבחרים",
    noCalendars: "לא נמצאו יומנים בחשבון המחובר.",
    currentEvent: "קורה עכשיו",
    nextEvent: "האירוע הבא",
    skip: "דלג",
    hideEvent: "הסתר אירוע",
    hiddenEvents: "אירועים מוסתרים",
    restore: "שחזור",
    remaining: "נותרו",
    untilNextCalendarEvent: "נותר עד לאירוע הבא ביומן",
    endsIn: "מסתיים בעוד",
    workOn: "על מה עובדים עכשיו?",
    chooseTask: "בחר/י משימה…",
    noTask: "עבודה ללא משימה",
    seconds: "הצג שניות בשעון",
    gregorianDate: "תאריך לועזי ויום בשבוע",
    weather: "מזג אוויר",
    hebrewDate: "תאריך עברי",
    weatherHighLow: "טמפרטורת מקסימום ומינימום יומית",
    highTemperature: "מקס׳",
    lowTemperature: "מינ׳",
    weatherUnavailable: "מזג האוויר לא זמין כרגע",
    clear: "בהיר",
    partlyCloudy: "מעונן חלקית",
    cloudy: "מעונן",
    rain: "גשם",
    storm: "סופה",
    snow: "שלג",
    lastSync: "סונכרן לאחרונה",
    estimate: "הערכה",
    estimatedEnd: "סיום משוער",
    minutes: "דקות",
    project: "פרויקט",
    allProjects: "ללא פרויקט",
    dragTask: "גרור לשינוי הסדר",
    timeSummary: "סיכום זמן לפי פרויקט",
    tracked: "נמדדו",
    entries: "רשומות",
    defaultTaskEstimate: "זמן ברירת מחדל למשימה",
    save: "שמור",
    toDo: "לביצוע",
    completedCount: "הושלמו",
    eventsToday: "אירועים היום",
    remainingToday: "נותרו",
    remainingEventsToday: "אירועים נותרו היום",
    chooseFromTasks: "בחרו משימה מרשימת המשימות כדי להתחיל.",
    noRemainingToday: "לא נותרו משימות היום",
    tomorrow: "מחר",
    scheduleForTomorrow: "למחר",
    scheduledForTomorrow: "מתוכננת למחר",
    countdown: "ספירה לאחור",
    nextCalendarEvent: "האירוע הבא ביומן",
    endOfDay: "סוף היום",
    customTime: "שעה מסוימת",
    timer: "טיימר",
    tickSound: "צליל תקתוק בכל שנייה",
    countdownInWords: "הצג את הזמן שנותר במילים",
    startTimer: "התחל טיימר",
    showCalendarAgenda: "הצגת אירועים נוספים ביומן",
    travelPlanning: "תכנון נסיעה",
    travelOrigin: "נקודת יציאה",
    travelMode: "אמצעי תחבורה",
    drive: "רכב",
    transit: "תחבורה ציבורית",
    travelLead: "זמן התארגנות לפני אירוע",
    travelCalendar: "יומן נסיעות",
    addTravel: "הוסף נסיעה ליומן",
  },
  en: {
    tagline: "Less noise. More focus.",
    signIn: "Join beta",
    calendar: "Calendars",
    calendarCopy:
      "Connect Google Calendar, then choose exactly which calendars belong in your workspace.",
    tasks: "Tasks",
    addTask: "New task",
    focus: "Your focus",
    noEvent: "You have room to focus",
    noEventCopy:
      "Nothing is coming up. Pick one task and work at your own pace.",
    start: "Start focus",
    stop: "Stop focus",
    today: "Today",
    active: "Working now",
    clock: "Your time",
    settings: "Settings",
    loading: "Loading your workspace…",
    inbox: "Inbox",
    completed: "Completed",
    loginTitle: "Your whole day, in one glance.",
    loginCopy: "Calendar, tasks and time tracking in one calm space.",
    google: "Continue with Google",
    apple: "Continue with Apple",
    magic: "Email magic link",
    email: "Email address",
    send: "Send me a link",
    invite: "Closed beta — invitation required.",
    appearance: "Appearance",
    system: "System",
    light: "Light",
    dark: "Dark",
    calendarSettings: "Calendar selection",
    calendarSettingsCopy:
      "Choose only the calendars that should appear in your workspace.",
    connectCalendar: "Connect Google Calendar",
    connected: "Connected",
    saveError: "We could not save that change. Please try again.",
    taskCreateError: "We could not add the task. Please try again.",
    deleteTask: "Delete task",
    deleteTaskConfirm:
      "Delete this task? Time already tracked for it will remain available.",
    accessibility: "Accessibility statement",
    selectedCalendars: "Selected calendars",
    noCalendars: "No calendars were found for this Google account.",
    currentEvent: "Happening now",
    nextEvent: "Next event",
    skip: "Skip",
    hideEvent: "Hide event",
    hiddenEvents: "Hidden events",
    restore: "Restore",
    remaining: "remaining",
    untilNextCalendarEvent: "until the next calendar event",
    endsIn: "Ends in",
    workOn: "What are you working on?",
    chooseTask: "Choose a task…",
    noTask: "Work without a task",
    seconds: "Show seconds on the clock",
    gregorianDate: "Gregorian date and weekday",
    weather: "Weather",
    hebrewDate: "Hebrew date",
    weatherHighLow: "Daily high and low temperatures",
    highTemperature: "High",
    lowTemperature: "Low",
    weatherUnavailable: "Weather is unavailable right now",
    clear: "Clear",
    partlyCloudy: "Partly cloudy",
    cloudy: "Cloudy",
    rain: "Rain",
    storm: "Storm",
    snow: "Snow",
    lastSync: "Last synced",
    estimate: "Estimate",
    estimatedEnd: "Estimated finish",
    minutes: "minutes",
    project: "Project",
    allProjects: "No project",
    dragTask: "Drag to reorder",
    timeSummary: "Time by project",
    tracked: "Tracked",
    entries: "entries",
    defaultTaskEstimate: "Default task duration",
    save: "Save",
    toDo: "to do",
    completedCount: "completed",
    eventsToday: "events today",
    remainingToday: "remaining",
    remainingEventsToday: "events remaining today",
    chooseFromTasks: "Choose a task from the task list to start.",
    noRemainingToday: "No events remain today",
    tomorrow: "Tomorrow",
    scheduleForTomorrow: "Tomorrow",
    scheduledForTomorrow: "Scheduled for tomorrow",
    countdown: "Countdown",
    nextCalendarEvent: "Next calendar event",
    endOfDay: "End of day",
    customTime: "Specific time",
    timer: "Timer",
    tickSound: "Tick sound every second",
    countdownInWords: "Show the remaining time in words",
    startTimer: "Start timer",
    showCalendarAgenda: "Show additional calendar events",
    travelPlanning: "Travel planning",
    travelOrigin: "Starting point",
    travelMode: "Transport",
    drive: "Driving",
    transit: "Public transit",
    travelLead: "Buffer before event",
    travelCalendar: "Travel calendar",
    addTravel: "Add travel to calendar",
  },
} as const;

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await nativeFetch(path, init);
  if (!response.ok)
    throw new Error(
      (await response.json().catch(() => null))?.message ?? "Request failed",
    );
  return response.status === 204 ? (undefined as T) : response.json();
}

function formatTime(
  value: string | Date,
  language: Language,
  showSeconds = false,
) {
  return new Intl.DateTimeFormat(language === "he" ? "he-IL" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: showSeconds ? "2-digit" : undefined,
    hour12: false,
  }).format(new Date(value));
}
function formatDuration(milliseconds: number) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function formatDurationInWords(milliseconds: number, language: Language) {
  if (milliseconds <= 0) return language === "he" ? "עכשיו" : "now";
  const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60_000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  const valueLabel = (
    value: number,
    he: [string, string, string],
    en: string,
  ) => {
    if (language === "en") return `${value} ${en}${value === 1 ? "" : "s"}`;
    if (value === 1) return he[0];
    if (value === 2) return he[1];
    return `${value} ${he[2]}`;
  };
  if (days) parts.push(valueLabel(days, ["יום", "יומיים", "ימים"], "day"));
  if (hours)
    parts.push(valueLabel(hours, ["שעה", "שעתיים", "שעות"], "hour"));
  if (minutes)
    parts.push(valueLabel(minutes, ["דקה", "שתי דקות", "דקות"], "minute"));
  const visibleParts = parts.slice(0, 2);
  if (language === "he") return `עוד ${visibleParts.join(" ו־")}`;
  return `in ${visibleParts.join(" and ")}`;
}
function dateInputValue(value: Date | string) {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function dateInputToIso(value: string) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12).toISOString();
}
function isScheduledForLater(dueAt: string | null, now: Date) {
  if (!dueAt) return false;
  const due = new Date(dueAt);
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return dueDay > today;
}
function countdownTarget(
  user: User,
  focus: Focus | null,
  now: Date,
): Date | null {
  if (user.countdownMode === "NEXT_EVENT")
    return focus?.nextEvent ? new Date(focus.nextEvent.startsAt) : null;
  if (user.countdownMode === "END_OF_DAY") {
    const end = new Date(now);
    end.setHours(24, 0, 0, 0);
    return end;
  }
  if (user.countdownMode === "CUSTOM_TIME") {
    if (!user.countdownTargetTime) return null;
    const [hours, minutes] = user.countdownTargetTime.split(":").map(Number);
    const target = new Date(now);
    target.setHours(hours, minutes, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    return target;
  }
  return user.countdownEndsAt ? new Date(user.countdownEndsAt) : null;
}
function timeGreeting(
  value: Date,
  name: string | null | undefined,
  language: Language,
) {
  const hour = value.getHours();
  const greeting =
    language === "he"
      ? hour < 12
        ? "בוקר טוב"
        : hour < 18
          ? "צהריים טובים"
          : "ערב טוב"
      : hour < 12
        ? "Good morning"
        : hour < 18
          ? "Good afternoon"
          : "Good evening";
  const displayName = name?.trim();
  return displayName ? `${greeting}, ${displayName}` : greeting;
}
function formatHebrewDate(value: Date) {
  return new Intl.DateTimeFormat("he-IL-u-ca-hebrew", {
    day: "numeric",
    month: "long",
  }).format(value);
}
function weatherLabel(code: number | null, text: Copy) {
  if (code === null) return text.weatherUnavailable;
  if (code === 0) return text.clear;
  if ([1, 2].includes(code)) return text.partlyCloudy;
  if (code === 3) return text.cloudy;
  if (
    [45, 48, 51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)
  )
    return text.rain;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return text.snow;
  return text.storm;
}
const fallbackColors = [
  "#0f6b44",
  "#2563eb",
  "#a855f7",
  "#db2777",
  "#d97706",
  "#0891b2",
  "#64748b",
];
function calendarColor(
  calendar: Calendar | CalendarEvent["calendar"],
  index = 0,
) {
  return calendar.color || fallbackColors[index % fallbackColors.length];
}

function App() {
  const [language, setLanguage] = useState<Language>("he");
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [focus, setFocus] = useState<Focus | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [calendars, setCalendars] = useState<CalendarState | null>(null);
  const [weather, setWeather] = useState<Weather | null>(null);
  const [spotify, setSpotify] = useState<SpotifyState | null>(null);
  const [travelEvent, setTravelEvent] = useState<CalendarEvent | null>(null);
  const [travelPlan, setTravelPlan] = useState<TravelPlan | null>(null);
  const [travelLoading, setTravelLoading] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [newTaskEstimate, setNewTaskEstimate] = useState(60);
  const [newTaskProject, setNewTaskProject] = useState("");
  const [newTaskDueAt, setNewTaskDueAt] = useState("");
  const [magicEmail, setMagicEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [attentionDaily, setAttentionDaily] = useState<AttentionDaily | null>(
    null,
  );
  const [attentionPanelMode, setAttentionPanelMode] = useState<
    AttentionPanelMode | "hidden"
  >(() => {
    const saved = localStorage.getItem("aiqel-attention-panel-mode");
    return saved === "small" ||
      saved === "half" ||
      saved === "large" ||
      saved === "camera-only" ||
      saved === "hidden"
      ? saved
      : "half";
  });
  const [attentionSplit, setAttentionSplit] = useState(() => {
    const saved = Number(localStorage.getItem("aiqel-attention-split"));
    return Number.isFinite(saved) && saved >= 28 && saved <= 72 ? saved : 50;
  });
  const audioContextRef = useRef<AudioContext | null>(null);
  const text = messages[language];
  const now = useClock();
  const clockCountdownTarget = user ? countdownTarget(user, focus, now) : null;
  const clockCountdown = clockCountdownTarget
    ? Math.max(0, clockCountdownTarget.getTime() - now.getTime())
    : 0;
  const clockCountdownLabel = user
    ? user.countdownMode === "NEXT_EVENT"
      ? focus?.nextEvent?.title ?? text.untilNextCalendarEvent
      : user.countdownMode === "END_OF_DAY"
        ? text.endOfDay
        : user.countdownMode === "CUSTOM_TIME"
          ? `${text.remaining} עד ${user.countdownTargetTime ?? "—"}`
          : text.timer
    : "";

  const loadCalendars = async () =>
    setCalendars(await api<CalendarState>("/api/calendar/calendars"));
  const loadWeather = async () => {
    try {
      setWeather(await api<Weather>("/api/weather"));
    } catch {
      setWeather(null);
    }
  };
  const loadSpotify = async () => {
    try {
      setSpotify(await api<SpotifyState>("/api/spotify"));
    } catch {
      setSpotify(null);
    }
  };
  const loadAttentionDaily = async () => {
    try {
      setAttentionDaily(
        await api<AttentionDaily>("/api/attention/daily"),
      );
    } catch {
      setAttentionDaily(null);
    }
  };
  const refresh = async () => {
    const auth = await api<{ user: User | null }>("/api/auth/me");
    setUser(auth.user);
    if (auth.user) {
      setLanguage(auth.user.locale === "EN" ? "en" : "he");
      await api("/api/calendar/sync", { method: "POST" }).catch(() => null);
      const [nextFocus, nextTasks, nextProjects] = await Promise.all([
        api<Focus>("/api/focus"),
        api<Task[]>("/api/tasks"),
        api<Project[]>("/api/projects"),
      ]);
      setFocus(nextFocus);
      setTasks(nextTasks);
      setProjects(nextProjects);
      await loadSpotify();
      await loadCalendars();
      setNewTaskEstimate(auth.user.defaultTaskEstimateMinutes);
      if (auth.user.showWeather) await loadWeather();
      else setWeather(null);
    }
  };
  useEffect(() => {
    void initializeNativeApp();
    refresh().catch((reason) => {
      setError(reason.message);
      setUser(null);
    });
  }, []);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!user) return;
    let disposed = false;
    const socket = io(isNative ? "https://focus.aiqel.io" : undefined, {
      path: "/socket.io",
      withCredentials: true,
      autoConnect: false,
    });
    void nativeToken().then((token) => {
      if (disposed) return;
      socket.auth = token ? { token } : {};
      socket.connect();
    });
    socket.on("task:changed", refresh);
    socket.on("timer:changed", refresh);
    socket.on("calendar:changed", refresh);
    socket.on("settings:changed", refresh);
    socket.on("client:changed", refresh);
    socket.on("project:changed", refresh);
    socket.on("attention:changed", loadAttentionDaily);
    return () => {
      disposed = true;
      socket.disconnect();
    };
  }, [user]);
  useEffect(() => {
    if (!user) return;
    void updateNativeWidget({
      updatedAt: new Date().toISOString(),
      activeTask:
        focus?.activeSession?.task?.title ?? focus?.activeSession?.note ?? null,
      activeSince: focus?.activeSession?.startedAt ?? null,
      nextEvent: focus?.nextEvent?.title ?? null,
      nextEventAt: focus?.nextEvent?.startsAt ?? null,
      remainingTasks: tasks.filter((task) => task.status !== "DONE").length,
    });
  }, [user, focus, tasks]);
  useEffect(() => {
    if (!user) return;
    const interval = window.setInterval(() => void loadSpotify(), 3_000);
    return () => window.clearInterval(interval);
  }, [user?.id]);
  useEffect(() => {
    if (!user?.showAttentionCamera || !user.attentionSyncEnabled) {
      setAttentionDaily(null);
      return;
    }
    void loadAttentionDaily();
  }, [user?.showAttentionCamera, user?.attentionSyncEnabled]);
  useEffect(() => {
    if (!user || !("wakeLock" in navigator)) return;
    let sentinel: { release: () => Promise<void> } | null = null;
    const wake = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        sentinel = await (
          navigator as Navigator & {
            wakeLock: {
              request: (
                type: "screen",
              ) => Promise<{ release: () => Promise<void> }>;
            };
          }
        ).wakeLock.request("screen");
      } catch {
        // iOS/Safari or a low-battery policy may deny the optional lock.
      }
    };
    void wake();
    document.addEventListener("visibilitychange", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      void sentinel?.release();
    };
  }, [user]);
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "he" ? "rtl" : "ltr";
  }, [language]);
  useEffect(() => {
    const theme = user?.theme ?? "SYSTEM";
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = theme === "DARK" || (theme === "SYSTEM" && media.matches);
      document.documentElement.classList.toggle("dark", dark);
      document.documentElement.style.colorScheme = dark ? "dark" : "light";
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [user?.theme]);
  useEffect(() => {
    if (!user?.countdownTicking || !clockCountdownTarget) return;
    let interval: number | undefined;
    const tick = () => {
      if (clockCountdownTarget.getTime() <= Date.now()) return;
      const context = audioContextRef.current;
      if (!context || context.state !== "running") return;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = 950;
      gain.gain.setValueAtTime(0.012, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        context.currentTime + 0.04,
      );
      oscillator.connect(gain).connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.045);
    };
    const initialDelay = 1000 - (Date.now() % 1000);
    const timeout = window.setTimeout(() => {
      tick();
      interval = window.setInterval(tick, 1000);
    }, initialDelay);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [user?.countdownTicking, clockCountdownTarget?.getTime()]);

  const countdown = focus?.nextEvent
    ? Math.max(0, new Date(focus.nextEvent.startsAt).getTime() - now.getTime())
    : 0;
  const currentRemaining = focus?.currentEvent
    ? Math.max(0, new Date(focus.currentEvent.endsAt).getTime() - now.getTime())
    : 0;
  const activeElapsed = focus?.activeSession
    ? now.getTime() - new Date(focus.activeSession.startedAt).getTime()
    : 0;
  const activeEstimateExceeded = Boolean(
    focus?.activeSession?.task?.estimateMinutes &&
    activeElapsed > focus.activeSession.task.estimateMinutes * 60 * 1000,
  );
  const addTask = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newTask.trim()) return;
    try {
      const task = await api<Task>("/api/tasks", {
        method: "POST",
        body: JSON.stringify({
          title: newTask,
          status: "INBOX",
          estimateMinutes: newTaskEstimate,
          projectId: newTaskProject || null,
          dueAt: dateInputToIso(newTaskDueAt),
        }),
      });
      setTasks((current) => [...current, task]);
      setNewTask("");
      setNewTaskProject("");
      setNewTaskDueAt("");
      setError("");
    } catch {
      setError(text.taskCreateError);
    }
  };
  const toggleTask = async (task: Task) => {
    const updated = await api<Task>(`/api/tasks/${task.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        status: task.status === "DONE" ? "TODO" : "DONE",
      }),
    });
    setTasks((current) =>
      current.map((item) => (item.id === task.id ? updated : item)),
    );
  };
  const startTask = async (task: Task) => {
    await api("/api/time/start", {
      method: "POST",
      body: JSON.stringify({ taskId: task.id }),
    });
    await refresh();
  };
  const stopTimer = async () => {
    try {
      await api("/api/time/stop", { method: "POST" });
      await refresh();
    } catch {
      setError(text.saveError);
    }
  };
  const deleteTask = async (task: Task) => {
    if (!window.confirm(text.deleteTaskConfirm)) return;
    try {
      if (focus?.activeSession?.task?.id === task.id)
        await api("/api/time/stop", { method: "POST" });
      await api(`/api/tasks/${task.id}`, { method: "DELETE" });
      setTasks((current) => current.filter((item) => item.id !== task.id));
      setError("");
      await refresh();
    } catch {
      setError(text.saveError);
    }
  };
  const deleteAccount = async () => {
    const confirmed = window.confirm(
      language === "he"
        ? "למחוק לצמיתות את החשבון ואת כל הנתונים? לא ניתן לבטל פעולה זו."
        : "Permanently delete your account and all its data? This cannot be undone.",
    );
    if (!confirmed) return;
    try {
      await api("/api/account", { method: "DELETE" });
      await clearNativeSession();
      setSettingsOpen(false);
      setUser(null);
      setFocus(null);
      setTasks([]);
    } catch {
      setError(text.saveError);
    }
  };
  const updateTask = async (
    task: Task,
    input: Partial<Pick<Task, "estimateMinutes" | "projectId" | "dueAt">>,
  ) => {
    const updated = await api<Task>(`/api/tasks/${task.id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    setTasks((current) =>
      current.map((item) => (item.id === task.id ? updated : item)),
    );
  };
  const saveTaskOrder = async (ordered: Task[]) => {
    setTasks(ordered);
    try {
      const saved = await api<Task[]>("/api/tasks/order", {
        method: "PUT",
        body: JSON.stringify({
          items: ordered.map((task, sortOrder) => ({ id: task.id, sortOrder })),
        }),
      });
      setTasks(saved);
    } catch {
      setError(text.saveError);
      await refresh();
    }
  };
  const dropTask = async (activeId: string, targetId: string) => {
    if (activeId === targetId) return;
    const from = tasks.findIndex((task) => task.id === activeId);
    const to = tasks.findIndex((task) => task.id === targetId);
    if (from < 0 || to < 0) return;
    const ordered = arrayMove(tasks, from, to);
    await saveTaskOrder(ordered);
  };
  const skipEvent = async (eventId: string) => {
    setFocus((current) => {
      if (!current) return current;
      const candidates = [
        current.nextEvent,
        ...current.todayEvents,
        ...(current.tomorrowEvents ?? []),
      ]
        .filter(
          (event): event is CalendarEvent =>
            Boolean(event) && event?.id !== eventId,
        )
        .sort(
          (a, b) =>
            new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
        );
      const currentEvent =
        current.currentEvent?.id === eventId ? null : current.currentEvent;
      return {
        ...current,
        currentEvent,
        nextEvent:
          candidates.find(
            (event) => new Date(event.startsAt).getTime() > Date.now(),
          ) ?? null,
        todayEvents: current.todayEvents.filter(
          (event) => event.id !== eventId,
        ),
        tomorrowEvents: (current.tomorrowEvents ?? []).filter(
          (event) => event.id !== eventId,
        ),
        todayRemaining: Math.max(
          0,
          current.todayRemaining -
            (current.todayEvents.some((event) => event.id === eventId) ? 1 : 0),
        ),
      };
    });
    try {
      await api(`/api/calendar/events/${eventId}/hide`, { method: "POST" });
      await refresh();
    } catch {
      setError(text.saveError);
      await refresh();
    }
  };
  const restoreHiddenEvent = async (eventId: string) => {
    try {
      await api(`/api/calendar/events/${eventId}/hide`, { method: "DELETE" });
      await refresh();
    } catch {
      setError(text.saveError);
    }
  };
  const sendMagic = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api("/api/auth/magic-link", {
        method: "POST",
        body: JSON.stringify({ email: magicEmail }),
      });
      setSent(true);
    } catch (reason) {
      setError((reason as Error).message);
    }
  };
  const updateSettings = async (
    input: Partial<
      Pick<
        User,
        | "theme"
        | "locale"
        | "showSeconds"
        | "showGregorianDate"
        | "showWeather"
        | "showHebrewDate"
        | "showWeatherHighLow"
        | "showSpotifyPlayer"
        | "showGreeting"
        | "showCalendarAgenda"
        | "showAttentionCamera"
        | "attentionSyncEnabled"
        | "defaultTaskEstimateMinutes"
        | "countdownMode"
        | "countdownTargetTime"
        | "countdownDurationMinutes"
        | "countdownEndsAt"
        | "countdownTicking"
        | "countdownInWords"
        | "travelMode"
        | "travelOrigin"
        | "travelLeadMinutes"
        | "travelCalendarId"
      >
    >,
  ) => {
    try {
      const settings = await api<
        Pick<
          User,
          | "theme"
          | "locale"
          | "showSeconds"
          | "showGregorianDate"
          | "showWeather"
          | "showHebrewDate"
          | "showWeatherHighLow"
          | "showSpotifyPlayer"
          | "showGreeting"
          | "showCalendarAgenda"
          | "showAttentionCamera"
          | "attentionSyncEnabled"
          | "weatherLocation"
          | "defaultTaskEstimateMinutes"
          | "countdownMode"
          | "countdownTargetTime"
          | "countdownDurationMinutes"
          | "countdownEndsAt"
          | "countdownTicking"
          | "countdownInWords"
          | "travelMode"
          | "travelOrigin"
          | "travelLeadMinutes"
          | "travelCalendarId"
        >
      >("/api/settings", { method: "PATCH", body: JSON.stringify(input) });
      setUser((current) => (current ? { ...current, ...settings } : current));
      if (input.showWeather !== undefined)
        input.showWeather ? loadWeather() : setWeather(null);
    } catch {
      setError(text.saveError);
    }
  };
  const setCountdownTicking = async (value: boolean) => {
    if (value) {
      const AudioContextConstructor =
        window.AudioContext ??
        (
          window as typeof window & {
            webkitAudioContext?: typeof AudioContext;
          }
        ).webkitAudioContext;
      if (AudioContextConstructor) {
        audioContextRef.current ??= new AudioContextConstructor();
        await audioContextRef.current.resume().catch(() => undefined);
      }
    }
    await updateSettings({ countdownTicking: value });
  };
  const spotifyCommand = async (
    action: "play" | "pause" | "next" | "previous" | "transfer",
    deviceId?: string,
  ) => {
    try {
      await api("/api/spotify/command", {
        method: "POST",
        body: JSON.stringify({ action, deviceId }),
      });
      await loadSpotify();
    } catch {
      setError(text.saveError);
    }
  };
  const planTravel = async (
    event: CalendarEvent,
    mode: "DRIVE" | "TRANSIT" = user?.travelMode ?? "DRIVE",
  ) => {
    setTravelEvent(event);
    setTravelLoading(true);
    setTravelPlan(null);
    try {
      const plan = await api<TravelPlan>(
        `/api/calendar/events/${event.id}/travel?mode=${mode}`,
      );
      setTravelPlan(plan);
    } catch (reason) {
      setError((reason as Error).message || text.saveError);
    } finally {
      setTravelLoading(false);
    }
  };
  const addTravelToCalendar = async () => {
    if (!travelEvent || !travelPlan) return;
    setTravelLoading(true);
    try {
      await api(`/api/calendar/events/${travelEvent.id}/travel`, {
        method: "POST",
        body: JSON.stringify({
          mode: travelPlan.mode,
          calendarId: user?.travelCalendarId ?? null,
        }),
      });
      setTravelEvent(null);
      setTravelPlan(null);
      await refresh();
    } catch (reason) {
      setError((reason as Error).message || text.saveError);
    } finally {
      setTravelLoading(false);
    }
  };
  const toggleCalendar = async (calendar: Calendar) => {
    if (!calendars) return;
    const selected = calendar.selected
      ? calendars.calendars
          .filter((item) => item.selected && item.id !== calendar.id)
          .map((item) => item.id)
      : [
          ...calendars.calendars
            .filter((item) => item.selected)
            .map((item) => item.id),
          calendar.id,
        ];
    if (!selected.length) return setError(text.saveError);
    try {
      const result = await api<{ calendars: Calendar[] }>(
        "/api/calendar/calendars/selection",
        { method: "PUT", body: JSON.stringify({ calendarIds: selected }) },
      );
      setCalendars({ ...calendars, calendars: result.calendars });
      await refresh();
    } catch {
      setError(text.saveError);
    }
  };

  const changeAttentionPanelMode = (
    mode: AttentionPanelMode | "hidden",
  ) => {
    localStorage.setItem("aiqel-attention-panel-mode", mode);
    setAttentionPanelMode(mode);
    const modeSplit =
      mode === "small" ? 34 : mode === "large" ? 62 : mode === "half" ? 50 : null;
    if (modeSplit !== null) {
      localStorage.setItem("aiqel-attention-split", String(modeSplit));
      setAttentionSplit(modeSplit);
    }
  };

  const updateAttentionSplit = (value: number) => {
    const next = Math.min(72, Math.max(28, Math.round(value)));
    localStorage.setItem("aiqel-attention-split", String(next));
    setAttentionSplit(next);
  };

  const beginAttentionResize = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    const workspace = event.currentTarget.parentElement;
    const camera = workspace?.querySelector<HTMLElement>(
      ".attention-camera-panel",
    );
    const product = workspace?.querySelector<HTMLElement>(
      ".attention-product",
    );
    if (!workspace || !camera || !product) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const bounds = workspace.getBoundingClientRect();
    const initialX = event.clientX;
    const initialSplit = attentionSplit;
    const cameraIsBeforeProduct =
      camera.getBoundingClientRect().left < product.getBoundingClientRect().left;
    const direction = cameraIsBeforeProduct ? 1 : -1;
    const onMove = (move: PointerEvent) => {
      updateAttentionSplit(
        initialSplit +
          (direction * (move.clientX - initialX) * 100) / bounds.width,
      );
    };
    const onEnd = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd, { once: true });
  };

  const resizeAttentionByKeyboard = (
    event: ReactKeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const keyboardDirection = language === "he" ? -1 : 1;
    updateAttentionSplit(
      attentionSplit +
        (event.key === "ArrowRight" ? keyboardDirection : -keyboardDirection) * 4,
    );
  };

  const recordAttentionSample = (sample: AttentionSample) => {
    if (!user?.attentionSyncEnabled) return;
    void api("/api/attention/samples", {
      method: "POST",
      body: JSON.stringify({
        workSessionId: focus?.activeSession?.id ?? null,
        samples: [
          {
            capturedAt: new Date(sample.at).toISOString(),
            durationSec: 5,
            focusScore: sample.score,
            state: sample.state.toUpperCase(),
          },
        ],
      }),
    })
      .then(() => void loadAttentionDaily())
      .catch(() => undefined);
  };

  const deleteAttentionData = async () => {
    const approved = window.confirm(
      language === "he"
        ? "למחוק את כל סיכומי זמן הריכוז שנשמרו בחשבון?"
        : "Delete all attention time summaries saved to your account?",
    );
    if (!approved) return;
    try {
      await api("/api/attention/data", { method: "DELETE" });
    } catch {
      setError(text.saveError);
    }
  };

  if (user === undefined)
    return (
      <main className="grid min-h-svh place-items-center bg-background text-foreground">
        {text.loading}
      </main>
    );
  if (!user)
    return (
      <Login
        language={language}
        text={text}
        setLanguage={setLanguage}
        email={magicEmail}
        setEmail={setMagicEmail}
        sent={sent}
        error={error}
        onSubmit={sendMagic}
      />
    );
  const attentionWorkspaceOpen =
    user.showAttentionCamera && attentionPanelMode !== "hidden";
  const navigation = (
    <nav
      className="focus-nav order-0 mx-auto flex w-full max-w-6xl flex-nowrap items-center justify-end gap-2 px-4 py-3 sm:gap-4 sm:px-8 sm:py-5"
      aria-label="תצוגה והגדרות"
    >
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        {user.showSpotifyPlayer && spotify?.connected && (
          <SpotifyMiniPlayer spotify={spotify} onCommand={spotifyCommand} />
        )}
        <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
          {user.showAttentionCamera && attentionPanelMode === "hidden" && (
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => changeAttentionPanelMode("half")}
              aria-label={language === "he" ? "הצגת מד הריכוז" : "Show attention meter"}
            >
              <EyeOff />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() =>
              updateSettings({ theme: user.theme === "DARK" ? "LIGHT" : "DARK" })
            }
            aria-label={user.theme === "DARK" ? text.light : text.dark}
          >
            {user.theme === "DARK" ? <Sun /> : <Moon />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => setSettingsOpen(true)}
            aria-label={text.settings}
          >
            <Settings2 />
          </Button>
        </div>
      </div>
    </nav>
  );
  return (
    <div className="flex min-h-svh flex-col overflow-x-hidden bg-background text-foreground">
      <a className="skip-link" href="#main-content">
        {language === "he" ? "דלג לתוכן הראשי" : "Skip to main content"}
      </a>
      {!attentionWorkspaceOpen && navigation}
      {settingsOpen && (
        <SettingsDialog
          text={text}
          user={user}
          calendars={calendars}
          language={language}
          onClose={() => setSettingsOpen(false)}
          onLanguage={() => {
            const next = language === "he" ? "en" : "he";
            setLanguage(next);
            updateSettings({ locale: next === "he" ? "HE" : "EN" });
          }}
          onTheme={(theme) => updateSettings({ theme })}
          onToggle={(key, value) => updateSettings({ [key]: value })}
          onDefaultEstimate={(value) =>
            updateSettings({ defaultTaskEstimateMinutes: value })
          }
          onCountdown={(input) => updateSettings(input)}
          onTicking={setCountdownTicking}
          onCalendar={toggleCalendar}
          onRestoreHiddenEvent={restoreHiddenEvent}
          onTravelPreferences={updateSettings}
          spotify={spotify}
          onSpotifyRefresh={loadSpotify}
          onDeleteAttentionData={deleteAttentionData}
          onDeleteAccount={deleteAccount}
        />
      )}
      <div
        className={`attention-workspace attention-workspace--${user.showAttentionCamera ? attentionPanelMode : "hidden"}`}
        style={
          { "--attention-camera-width": `${attentionSplit}%` } as CSSProperties
        }
      >
        {user.showAttentionCamera && attentionPanelMode !== "hidden" && (
          <AttentionCameraPanel
            className="attention-camera-panel"
            language={language}
            mode={attentionPanelMode}
            dailyTotals={
              attentionDaily
                ? {
                    focused: attentionDaily.focusedSeconds,
                    away: attentionDaily.awaySeconds,
                    unknown: attentionDaily.unknownSeconds,
                  }
                : null
            }
            onModeChange={changeAttentionPanelMode}
            onSample={recordAttentionSample}
          />
        )}
        {attentionWorkspaceOpen && attentionPanelMode !== "camera-only" && (
          <div
            className="attention-splitter"
            role="separator"
            aria-orientation="vertical"
            aria-label={language === "he" ? "שינוי רוחב אזור המצלמה" : "Resize camera area"}
            aria-valuemin={28}
            aria-valuemax={72}
            aria-valuenow={attentionSplit}
            tabIndex={0}
            onPointerDown={beginAttentionResize}
            onKeyDown={resizeAttentionByKeyboard}
          >
            <span aria-hidden="true" />
          </div>
        )}
        <div
          className={
            user.showAttentionCamera && attentionPanelMode === "camera-only"
              ? "hidden"
              : "attention-product min-w-0"
          }
        >
      {attentionWorkspaceOpen && navigation}
      <header className="focus-clock-region relative order-1 mx-auto grid min-h-[clamp(19rem,42svh,32rem)] w-full max-w-6xl place-items-center px-16 py-10 text-center sm:px-24">
        <div className="min-w-0">
          {user.showGreeting && (
            <p className="mb-3 text-sm font-medium text-muted-foreground sm:text-base">
              {timeGreeting(
                now,
                user.name ?? user.email.split("@")[0],
                language,
              )}
            </p>
          )}
          {clockCountdownTarget && (
            <div className="mb-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>{clockCountdownLabel}</span>
              <span
                dir={
                  user.countdownInWords
                    ? language === "he"
                      ? "rtl"
                      : "ltr"
                    : "ltr"
                }
                className="font-sans font-semibold tabular-nums text-foreground"
              >
                {user.countdownInWords
                  ? formatDurationInWords(clockCountdown, language)
                  : formatDuration(clockCountdown)}
              </span>
            </div>
          )}
          <time
            className="header-clock block whitespace-nowrap"
            dateTime={now.toISOString()}
            dir="ltr"
          >
            {formatTime(now, language, user.showSeconds)}
          </time>
          {(user.showGregorianDate ||
            user.showHebrewDate ||
            user.showWeather) && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-muted-foreground/70">
              {user.showGregorianDate && (
                <span>
                  {new Intl.DateTimeFormat(
                    language === "he" ? "he-IL" : "en-US",
                    {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                    },
                  ).format(now)}
                </span>
              )}
              {user.showHebrewDate && <span>{formatHebrewDate(now)}</span>}
              {user.showWeather && (
                <WeatherInline
                  weather={weather}
                  text={text}
                  showHighLow={user.showWeatherHighLow}
                />
              )}
            </div>
          )}
        </div>
      </header>
      <main id="main-content" tabIndex={-1} className="contents">
        <section className="focus-work-section order-2 mx-auto w-full max-w-6xl px-4 pt-4 sm:px-8">
          <Card className="focus-card min-w-0 border-0 shadow-none">
            <CardHeader className="px-0 pb-0">
              <p className="mb-3 text-sm text-muted-foreground">
                {text.tasks}:{" "}
                {
                  tasks.filter(
                    (task) =>
                      task.status !== "DONE" && task.status !== "ARCHIVED",
                  ).length
                }{" "}
                {text.toDo} ·{" "}
                {tasks.filter((task) => task.status === "DONE").length}{" "}
                {text.completedCount} · {focus?.todayRemaining ?? 0}{" "}
                {text.remainingEventsToday}
              </p>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground">
                    {text.active}
                  </p>
                  <CardTitle className="mt-2 break-words text-2xl sm:text-3xl">
                    {focus?.activeSession?.task?.title ??
                      focus?.activeSession?.note ??
                      text.chooseFromTasks}
                  </CardTitle>
                </div>
                <Badge
                  variant="outline"
                  className="shrink-0 gap-1.5 border-0 bg-transparent"
                >
                  <span
                    className={`size-1.5 rounded-full ${focus?.activeSession ? "bg-emerald-500" : "bg-muted-foreground"}`}
                  />
                  {focus?.activeSession ? "LIVE" : "CALM"}
                </Badge>
              </div>
              {focus?.activeSession && (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span
                    dir="ltr"
                    className={`font-mono text-2xl ${
                      activeEstimateExceeded
                        ? "overtime-pulse"
                        : "text-foreground"
                    }`}
                  >
                    {formatDuration(activeElapsed)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={stopTimer}
                  >
                    <Pause />
                    {text.stop}
                  </Button>
                </div>
              )}
            </CardHeader>
          </Card>
        </section>
        <section className="focus-panels order-3 mx-auto mt-3 grid w-full max-w-6xl items-start gap-8 px-4 pt-6 pb-12 sm:px-8 lg:grid-cols-2">
          <TaskBoard
            tasks={tasks}
            projects={projects}
            text={text}
            now={now}
            activeTaskId={focus?.activeSession?.task?.id ?? null}
            newTask={newTask}
            setNewTask={setNewTask}
            newTaskEstimate={newTaskEstimate}
            setNewTaskEstimate={setNewTaskEstimate}
            newTaskProject={newTaskProject}
            setNewTaskProject={setNewTaskProject}
            newTaskDueAt={newTaskDueAt}
            setNewTaskDueAt={setNewTaskDueAt}
            activeElapsed={activeElapsed}
            onAdd={addTask}
            onToggle={toggleTask}
            onStart={startTask}
            onDelete={deleteTask}
            onUpdate={updateTask}
            onReorder={dropTask}
          />
          <CalendarPanel
            focus={focus}
            calendars={calendars}
            text={text}
            language={language}
            currentRemaining={currentRemaining}
            countdown={countdown}
            showAgenda={user.showCalendarAgenda}
            onSkip={skipEvent}
            onPlanTravel={planTravel}
            onOpenSettings={() => setSettingsOpen(true)}
          />
        </section>
        {error && (
          <p
            className="order-4 mx-auto mt-5 w-full max-w-6xl px-4 text-sm text-destructive sm:px-8"
            role="alert"
          >
            {error}
          </p>
        )}
      </main>
        </div>
      </div>
      {travelEvent && (
        <TravelDialog
          event={travelEvent}
          plan={travelPlan}
          loading={travelLoading}
          text={text}
          onMode={(mode) => void planTravel(travelEvent, mode)}
          onAdd={() => void addTravelToCalendar()}
          onClose={() => {
            setTravelEvent(null);
            setTravelPlan(null);
          }}
        />
      )}
    </div>
  );
}

function TaskBoard({
  tasks,
  projects,
  text,
  now,
  activeTaskId,
  newTask,
  setNewTask,
  newTaskEstimate,
  setNewTaskEstimate,
  newTaskProject,
  setNewTaskProject,
  newTaskDueAt,
  setNewTaskDueAt,
  activeElapsed,
  onAdd,
  onToggle,
  onStart,
  onDelete,
  onUpdate,
  onReorder,
}: {
  tasks: Task[];
  projects: Project[];
  text: Copy;
  now: Date;
  activeTaskId: string | null;
  newTask: string;
  setNewTask: (value: string) => void;
  newTaskEstimate: number;
  setNewTaskEstimate: (value: number) => void;
  newTaskProject: string;
  setNewTaskProject: (value: string) => void;
  newTaskDueAt: string;
  setNewTaskDueAt: (value: string) => void;
  activeElapsed: number;
  onAdd: (event: React.FormEvent) => void;
  onToggle: (task: Task) => void;
  onStart: (task: Task) => void;
  onDelete: (task: Task) => void;
  onUpdate: (
    task: Task,
    input: Partial<Pick<Task, "estimateMinutes" | "projectId" | "dueAt">>,
  ) => void;
  onReorder: (activeId: string, targetId: string) => void;
}) {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  let remainingMinutes = 0;
  const activeTasks = tasks.filter(
    (task) => task.status !== "DONE" && task.status !== "ARCHIVED",
  );
  const completedTasks = tasks.filter((task) => task.status === "DONE");
  const estimatedEnds = new Map<string, Date>();
  for (const task of tasks) {
    const scheduledForLater = isScheduledForLater(task.dueAt, now);
    const active = activeTaskId === task.id;
    if (
      task.status !== "DONE" &&
      task.status !== "ARCHIVED" &&
      (!scheduledForLater || active)
    ) {
      const taskRemainingMinutes = active
        ? Math.max(0, task.estimateMinutes - activeElapsed / 60_000)
        : task.estimateMinutes;
      remainingMinutes += taskRemainingMinutes;
      estimatedEnds.set(
        task.id,
        new Date(now.getTime() + remainingMinutes * 60_000),
      );
    }
  }
  const estimatedFinish = new Date(now.getTime() + remainingMinutes * 60_000);
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id)
      onReorder(String(active.id), String(over.id));
  };
  return (
    <Card className="min-w-0 bg-transparent shadow-none [--card-spacing:--spacing(6)]">
      <CardHeader className="pt-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {text.inbox}
            </p>
            <CardTitle>{text.tasks}</CardTitle>
            <CardDescription>
              {activeTasks.length} {text.toDo} · {completedTasks.length}{" "}
              {text.completedCount}
            </CardDescription>
          </div>
          <div className="shrink-0 text-center">
            <p className="text-xs text-muted-foreground">{text.estimatedEnd}</p>
            <time dir="ltr" className="font-mono text-lg font-semibold">
              {formatTime(estimatedFinish, "en", false)}
            </time>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="space-y-2" onSubmit={onAdd}>
          <div className="task-quick-add flex items-center gap-2">
            <input
              className="h-11 min-w-0 flex-1 border-0 bg-transparent px-1 text-sm outline-none"
              value={newTask}
              onChange={(event) => setNewTask(event.target.value)}
              aria-label={text.addTask}
              placeholder={text.addTask}
            />
            <Button
              type="submit"
              size="icon"
              className="size-8 shrink-0 rounded-full"
              aria-label={text.addTask}
            >
              <Plus />
            </Button>
          </div>
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            onClick={() => setOptionsOpen((value) => !value)}
          >
            {optionsOpen ? "הסתר מאפיינים" : "הוספת פרויקט וזמן משוער"}
          </button>
          {optionsOpen && (
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className="minimal-field h-10 text-sm"
                type="number"
                min={1}
                max={1440}
                value={newTaskEstimate}
                onChange={(event) =>
                  setNewTaskEstimate(
                    Math.max(1, Number(event.target.value) || 1),
                  )
                }
                aria-label={text.estimate}
              />
              <select
                className="minimal-field h-10 min-w-0 text-sm"
                value={newTaskProject}
                onChange={(event) => setNewTaskProject(event.target.value)}
                aria-label={text.project}
              >
                <option value="">{text.allProjects}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2 sm:col-span-2">
                <input
                  className="minimal-field h-10 min-w-0 flex-1 text-sm"
                  type="date"
                  min={dateInputValue(now)}
                  value={newTaskDueAt}
                  onChange={(event) => setNewTaskDueAt(event.target.value)}
                  aria-label={text.scheduleForTomorrow}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setNewTaskDueAt(
                      dateInputValue(
                        new Date(now.getTime() + 24 * 60 * 60 * 1000),
                      ),
                    )
                  }
                >
                  {text.scheduleForTomorrow}
                </Button>
              </div>
            </div>
          )}
        </form>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={tasks.map((task) => task.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="task-list">
              {tasks.map((task) => (
                <SortableTaskRow
                  key={task.id}
                  task={task}
                  projects={projects}
                  text={text}
                  active={activeTaskId === task.id}
                  scheduledForLater={isScheduledForLater(task.dueAt, now)}
                  estimatedEnd={estimatedEnds.get(task.id) ?? now}
                  onToggle={onToggle}
                  onStart={onStart}
                  onDelete={onDelete}
                  onUpdate={onUpdate}
                />
              ))}
              {!tasks.length && (
                <li className="flex min-h-32 items-center gap-2 text-sm text-muted-foreground">
                  <ListTodo className="size-5 shrink-0" />
                  {text.noEventCopy}
                </li>
              )}
            </ul>
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
}

function SortableTaskRow({
  task,
  projects,
  text,
  active,
  scheduledForLater,
  estimatedEnd,
  onToggle,
  onStart,
  onDelete,
  onUpdate,
}: {
  task: Task;
  projects: Project[];
  text: Copy;
  active: boolean;
  scheduledForLater: boolean;
  estimatedEnd: Date;
  onToggle: (task: Task) => void;
  onStart: (task: Task) => void;
  onDelete: (task: Task) => void;
  onUpdate: (
    task: Task,
    input: Partial<Pick<Task, "estimateMinutes" | "projectId" | "dueAt">>,
  ) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 20 : undefined,
      }}
      className={`task-row group relative rounded-lg px-2 py-2.5 transition-[background-color,color,opacity,transform] ${
        active ? "text-primary" : ""
      } ${isDragging ? "z-20 rounded-xl bg-card opacity-90 shadow-lg" : ""}`}
    >
      <div className="flex items-start gap-2.5">
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="task-drag-handle mt-1 shrink-0 touch-none cursor-grab text-muted-foreground active:cursor-grabbing"
          aria-label={`${text.dragTask}: ${task.title}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        <Checkbox
          className="task-checkbox mt-0.5 rounded-full"
          checked={task.status === "DONE"}
          onCheckedChange={() => onToggle(task)}
          aria-label={`${text.completed}: ${task.title}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={`min-w-0 break-words font-medium ${
                task.status === "DONE"
                  ? "text-muted-foreground line-through"
                  : ""
              }`}
            >
              {task.title}
            </span>
            {active && (
              <Badge variant="secondary" className="gap-1">
                <span className="size-1.5 rounded-full bg-emerald-500" />
                LIVE
              </Badge>
            )}
            {scheduledForLater && (
              <Badge variant="outline" className="text-muted-foreground">
                {text.tomorrow}
              </Badge>
            )}
          </div>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {task.project && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: task.project.color ?? "#64748b" }}
                />
                {task.project.name}
              </span>
            )}
            <span>
              {task.estimateMinutes} {text.minutes}
            </span>
            {scheduledForLater ? (
              <span>{text.scheduledForTomorrow}</span>
            ) : (
              <span>
                {text.estimatedEnd}{" "}
                <time dir="ltr" className="font-mono">
                  {formatTime(estimatedEnd, "en", false)}
                </time>
              </span>
            )}
          </p>
          <details className="task-details mt-1 text-xs text-muted-foreground">
            <summary className="w-fit cursor-pointer select-none">
              פרטים
            </summary>
            <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_7rem]">
              <select
                className="minimal-field h-8 min-w-0 text-xs"
                value={task.projectId ?? ""}
                onChange={(event) =>
                  onUpdate(task, { projectId: event.target.value || null })
                }
                aria-label={`${text.project}: ${task.title}`}
              >
                <option value="">{text.allProjects}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <input
                className="minimal-field h-8 text-xs"
                type="number"
                min={1}
                max={1440}
                defaultValue={task.estimateMinutes}
                onBlur={(event) => {
                  const estimateMinutes = Math.max(
                    1,
                    Number(event.target.value) || 1,
                  );
                  if (estimateMinutes !== task.estimateMinutes)
                    onUpdate(task, { estimateMinutes });
                }}
                aria-label={`${text.estimate}: ${task.title}`}
              />
              <input
                className="minimal-field h-8 min-w-0 text-xs sm:col-span-2"
                type="date"
                value={task.dueAt ? dateInputValue(task.dueAt) : ""}
                onChange={(event) =>
                  onUpdate(task, {
                    dueAt: dateInputToIso(event.target.value),
                  })
                }
                aria-label={text.scheduleForTomorrow}
              />
            </div>
          </details>
        </div>
        <div className="task-actions flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 rounded-full text-primary"
            onClick={() => onStart(task)}
            disabled={task.status === "DONE" || active}
            aria-label={text.start}
          >
            {active ? <Pause /> : <CirclePlay />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 rounded-full text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(task)}
            aria-label={`${text.deleteTask}: ${task.title}`}
          >
            <Trash2 />
          </Button>
        </div>
      </div>
    </li>
  );
}

function CalendarPanel({
  focus,
  calendars,
  text,
  language,
  currentRemaining,
  countdown,
  showAgenda,
  onSkip,
  onPlanTravel,
  onOpenSettings,
}: {
  focus: Focus | null;
  calendars: CalendarState | null;
  text: Copy;
  language: Language;
  currentRemaining: number;
  countdown: number;
  showAgenda: boolean;
  onSkip: (id: string) => void;
  onPlanTravel: (event: CalendarEvent) => void;
  onOpenSettings: () => void;
}) {
  const highlighted =
    focus?.currentEvent ??
    focus?.todayEvents.find((event) => event.id === focus?.nextEvent?.id);
  const highlightedLabel = focus?.currentEvent
    ? text.currentEvent
    : text.nextEvent;
  const highlightedRemaining = focus?.currentEvent
    ? currentRemaining
    : countdown;
  const remainingToday =
    focus?.todayEvents.filter((event) => event.id !== highlighted?.id) ?? [];
  return (
    <Card className="min-w-0 bg-transparent shadow-none [--card-spacing:--spacing(6)]">
      <CardHeader className="pt-0 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {text.calendar}
            </p>
            <CardTitle>{text.today}</CardTitle>
            <CardDescription>
              {focus?.todayTotal ?? 0} {text.eventsToday} ·{" "}
              {focus?.todayRemaining ?? 0} {text.remainingToday}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline">
              {focus?.todayTotal ?? 0}/{focus?.todayRemaining ?? 0}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={onOpenSettings}
              aria-label={text.calendarSettings}
            >
              <Settings2 />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!calendars?.connected ? (
          <div className="py-4 text-sm text-muted-foreground">
            <p>{text.calendarCopy}</p>
            <Button
              className="mt-3"
              size="sm"
              variant="outline"
              onClick={onOpenSettings}
            >
              {text.connectCalendar}
            </Button>
          </div>
        ) : (
          <>
            {highlighted && (
              <div
                className="pb-4"
                style={{
                  borderInlineStart: `3px solid ${calendarColor(highlighted.calendar)}`,
                  paddingInlineStart: 12,
                }}
              >
                <p className="text-xs text-muted-foreground">
                  {highlightedLabel}
                </p>
                <p className="mt-1 break-words text-lg font-semibold">
                  {highlighted.title}
                </p>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: calendarColor(highlighted.calendar),
                      }}
                      aria-label={highlighted.calendar.summary}
                      title={highlighted.calendar.summary}
                    />
                    <time dir="ltr" className="font-mono">
                      {formatTime(highlighted.startsAt, language, false)}–
                      {formatTime(highlighted.endsAt, language, false)}
                    </time>
                  </span>
                  <span className="flex items-center gap-2">
                    <span
                      dir="ltr"
                      className="font-mono font-semibold text-foreground"
                    >
                      {formatDuration(highlightedRemaining)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSkip(highlighted.id)}
                    >
                      <SkipForward className="rtl:rotate-180" />
                      {text.skip}
                    </Button>
                    <EventActions
                      event={highlighted}
                      onHide={onSkip}
                      onPlanTravel={onPlanTravel}
                    />
                  </span>
                </div>
              </div>
            )}
            {showAgenda && (
              <ol>
                {remainingToday.map((event, index) => (
                  <li
                    key={event.id}
                    className="flex min-w-0 items-center gap-3 py-2.5"
                    style={{
                      borderInlineStart: `2px solid ${calendarColor(event.calendar, index)}`,
                      paddingInlineStart: 10,
                    }}
                  >
                    <time
                      dir="ltr"
                      className="w-16 shrink-0 font-mono text-xs text-muted-foreground"
                    >
                      {formatTime(event.startsAt, language, false)}
                    </time>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {event.title}
                    </span>
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: calendarColor(event.calendar, index),
                      }}
                      aria-label={event.calendar.summary}
                      title={event.calendar.summary}
                    />
                    <EventActions
                      event={event}
                      compact
                      onHide={onSkip}
                      onPlanTravel={onPlanTravel}
                    />
                  </li>
                ))}
                {!remainingToday.length && (
                  <li className="py-5 text-sm text-muted-foreground">
                    {text.noRemainingToday}
                  </li>
                )}
              </ol>
            )}
            {showAgenda && (focus?.tomorrowEvents?.length ?? 0) > 0 && (
              <section className="mt-5">
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  {text.tomorrow}
                </p>
                <ol>
                  {focus?.tomorrowEvents.slice(0, 5).map((event, index) => (
                    <li
                      key={event.id}
                      className="flex min-w-0 items-center gap-3 py-2.5"
                      style={{
                        borderInlineStart: `2px solid ${calendarColor(event.calendar, index)}`,
                        paddingInlineStart: 10,
                      }}
                    >
                      <time
                        dir="ltr"
                        className="w-16 shrink-0 font-sans text-xs tabular-nums text-muted-foreground"
                      >
                        {formatTime(event.startsAt, language, false)}
                      </time>
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {event.title}
                      </span>
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor: calendarColor(event.calendar, index),
                        }}
                        aria-label={event.calendar.summary}
                        title={event.calendar.summary}
                      />
                      <EventActions
                        event={event}
                        compact
                        onHide={onSkip}
                        onPlanTravel={onPlanTravel}
                      />
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EventActions({
  event,
  compact = false,
  onHide,
  onPlanTravel,
}: {
  event: CalendarEvent;
  compact?: boolean;
  onHide?: (eventId: string) => void;
  onPlanTravel?: (event: CalendarEvent) => void;
}) {
  return (
    <span className="flex shrink-0 items-center gap-1">
      {event.location &&
        (onPlanTravel ? (
          <Button
            variant="ghost"
            size="sm"
            className={compact ? "size-8 px-0" : ""}
            onClick={() => onPlanTravel(event)}
            aria-label="תכנון נסיעה"
          >
            <CarFront />
            {!compact && "תכנון נסיעה"}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className={compact ? "size-8 px-0" : ""}
            render={
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.location)}`}
                target="_blank"
                rel="noreferrer"
                aria-label="תכנון נסיעה"
              />
            }
          >
            <MapPin />
            {!compact && "תכנון נסיעה"}
          </Button>
        ))}
      {event.conferenceUrl && (
        <Button
          variant="ghost"
          size="sm"
          className={compact ? "size-8 px-0" : ""}
          render={
            <a
              href={event.conferenceUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="הצטרפות לאירוע"
            />
          }
        >
          <Video />
          {!compact && "הצטרפות לאירוע"}
        </Button>
      )}
      {onHide && (
        <Button
          variant="ghost"
          size="sm"
          className={compact ? "size-8 px-0" : ""}
          onClick={() => onHide(event.id)}
          aria-label="הסתר אירוע"
        >
          <EyeOff />
          {!compact && "הסתר"}
        </Button>
      )}
    </span>
  );
}

function EventCard({
  event,
  label,
  text,
  language,
  now,
  remaining,
  onSkip,
  live = false,
}: {
  event: CalendarEvent | null | undefined;
  label: string;
  text: Copy;
  language: Language;
  now: Date;
  remaining: number;
  onSkip: (eventId: string) => void;
  live?: boolean;
}) {
  return (
    <Card
      className={`min-w-0 ${live ? "border-primary/30 bg-primary/[0.04]" : ""}`}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-medium tracking-[0.16em] text-muted-foreground uppercase">
              {label}
            </p>
            <CardTitle className="mt-4 break-words text-2xl sm:text-3xl">
              {event?.title ?? text.noEvent}
            </CardTitle>
          </div>
          {event && (
            <span
              className="mt-1 size-3 shrink-0 rounded-full"
              style={{ backgroundColor: calendarColor(event.calendar) }}
              title={event.calendar.summary}
            />
          )}
        </div>
        <CardDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-base">
          {event ? (
            <>
              <time
                dir="ltr"
                className="font-mono font-semibold text-foreground"
              >
                {formatTime(event.startsAt, language, false)}
                {event.endsAt &&
                  ` – ${formatTime(event.endsAt, language, false)}`}
              </time>
              <span>{event.calendar.summary}</span>
            </>
          ) : (
            text.noEventCopy
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {event ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div
              dir="ltr"
              className="font-mono text-2xl font-bold text-foreground"
            >
              {formatDuration(remaining)}{" "}
              <small
                dir="rtl"
                className="font-sans text-xs font-normal text-muted-foreground"
              >
                {live ? text.endsIn : text.remaining}
              </small>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSkip(event.id)}
            >
              <SkipForward />
              {text.skip}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarDays className="size-4" />
            {text.calendarCopy}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WeatherPill({
  weather,
  text,
}: {
  weather: Weather | null;
  text: Copy;
}) {
  return (
    <div className="mt-5 inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground shadow-sm">
      <CloudSun className="size-4 shrink-0 text-primary" />
      {weather ? (
        <span className="truncate">
          {weather.location} ·{" "}
          {weather.temperature === null
            ? "—"
            : `${Math.round(weather.temperature)}°C`}{" "}
          · {weatherLabel(weather.weatherCode, text)}
        </span>
      ) : (
        <span>{text.weatherUnavailable}</span>
      )}
    </div>
  );
}

function WeatherInline({
  weather,
  text,
  showHighLow,
}: {
  weather: Weather | null;
  text: Copy;
  showHighLow: boolean;
}) {
  return (
    <span className="inline-flex flex-wrap items-center justify-center gap-x-1">
      <CloudSun className="size-3.5 text-current" />
      {weather ? (
        <>
          <span>
            {weather.location} ·{" "}
            {weather.temperature === null
              ? "—"
              : `${Math.round(weather.temperature)}°C`}
          </span>
          {showHighLow && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                {text.highTemperature}{" "}
                {weather.temperatureMax === null
                  ? "—"
                  : `${Math.round(weather.temperatureMax)}°`}
              </span>
              <span aria-hidden="true">·</span>
              <span>
                {text.lowTemperature}{" "}
                {weather.temperatureMin === null
                  ? "—"
                  : `${Math.round(weather.temperatureMin)}°`}
              </span>
            </>
          )}
        </>
      ) : (
        text.weatherUnavailable
      )}
    </span>
  );
}

function SettingsDialog({
  text,
  user,
  calendars,
  language,
  onClose,
  onLanguage,
  onTheme,
  onToggle,
  onDefaultEstimate,
  onCountdown,
  onTicking,
  onCalendar,
  onRestoreHiddenEvent,
  onTravelPreferences,
  spotify,
  onSpotifyRefresh,
  onDeleteAttentionData,
  onDeleteAccount,
}: {
  text: Copy;
  user: User;
  calendars: CalendarState | null;
  language: Language;
  onClose: () => void;
  onLanguage: () => void;
  onTheme: (theme: Theme) => void;
  onToggle: (
    key:
      | "showSeconds"
      | "showGregorianDate"
      | "showWeather"
      | "showHebrewDate"
      | "showWeatherHighLow"
      | "showSpotifyPlayer"
      | "showGreeting"
      | "showCalendarAgenda"
      | "showAttentionCamera"
      | "attentionSyncEnabled"
      | "countdownInWords",
    value: boolean,
  ) => void;
  onDefaultEstimate: (value: number) => void;
  onCountdown: (
    input: Partial<
      Pick<
        User,
        | "countdownMode"
        | "countdownTargetTime"
        | "countdownDurationMinutes"
        | "countdownEndsAt"
      >
    >,
  ) => void;
  onTicking: (value: boolean) => void;
  onCalendar: (calendar: Calendar) => void;
  onRestoreHiddenEvent: (eventId: string) => void;
  onTravelPreferences: (
    input: Partial<
      Pick<
        User,
        "travelMode" | "travelOrigin" | "travelLeadMinutes" | "travelCalendarId"
      >
    >,
  ) => void;
  spotify: SpotifyState | null;
  onSpotifyRefresh: () => Promise<void>;
  onDeleteAttentionData: () => Promise<void>;
  onDeleteAccount: () => Promise<void>;
}) {
  const themes: Array<{ value: Theme; label: string; icon: typeof Sun }> = [
    { value: "LIGHT", label: text.light, icon: Sun },
    { value: "DARK", label: text.dark, icon: Moon },
    { value: "SYSTEM", label: text.system, icon: TimerReset },
  ];
  const [estimate, setEstimate] = useState(
    String(user.defaultTaskEstimateMinutes),
  );
  const [clockMode, setClockMode] = useState<CountdownMode>(user.countdownMode);
  const [clockTime, setClockTime] = useState(user.countdownTargetTime ?? "");
  const [clockDuration, setClockDuration] = useState(
    String(user.countdownDurationMinutes),
  );
  const [travelOrigin, setTravelOrigin] = useState(user.travelOrigin);
  const [travelLead, setTravelLead] = useState(String(user.travelLeadMinutes));
  const [travelMode, setTravelMode] = useState(user.travelMode);
  const [travelCalendarId, setTravelCalendarId] = useState(
    user.travelCalendarId ?? "",
  );
  const [view, setView] = useState<
    "general" | "calendar" | "integrations" | "projects" | "time"
  >("general");
  const [clients, setClients] = useState<
    Array<{ id: string; name: string; projects: Project[] }>
  >([]);
  const [entries, setEntries] = useState<
    Array<{
      id: string;
      note: string | null;
      durationSec: number;
      startedAt: string;
      project?: { name: string; client?: { name: string } | null } | null;
      task?: { title: string } | null;
    }>
  >([]);
  const loadClients = async () => {
    try {
      setClients(
        await api<Array<{ id: string; name: string; projects: Project[] }>>(
          "/api/clients",
        ),
      );
    } catch {
      setClients([]);
    }
  };
  useEffect(() => {
    if (view === "projects") void loadClients();
    if (view === "time")
      api<typeof entries>("/api/time/entries")
        .then(setEntries)
        .catch(() => setEntries([]));
  }, [view]);
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default bg-black/15 backdrop-blur-[1px]"
        onClick={onClose}
        aria-label="סגירת הגדרות"
      />
      <aside
        className="settings-sheet z-50 w-[min(100%,34rem)] overflow-hidden bg-popover text-popover-foreground shadow-2xl"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        <header className="mb-4 flex shrink-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="brand mb-1">
              <span className="brand-mark">A</span>
              <span>AIqel</span>
              <strong>Focus</strong>
            </div>
            <h2 id="settings-title" className="text-base font-medium">
              {text.settings}
            </h2>
            <p className="text-sm text-muted-foreground">
              הגדרות המרחב האישי שלך
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={onClose}
            aria-label="סגירה"
          >
            <X />
          </Button>
        </header>
        <div
          className="settings-tabs -mx-1 flex shrink-0 gap-1 overflow-x-auto px-1 pb-1"
          role="tablist"
          aria-label={text.settings}
        >
          {(
            [
              ["general", "כללי"],
              ["calendar", "יומנים"],
              ["integrations", "חיבורים"],
              ["projects", "לקוחות"],
              ["time", "זמנים"],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              role="tab"
              aria-selected={view === value}
              variant={view === value ? "secondary" : "ghost"}
              size="sm"
              className="shrink-0"
              onClick={() => setView(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pt-2" role="tabpanel">
          {view === "general" && (
            <section className="space-y-5">
              <Button
                variant="ghost"
                className="w-full justify-between px-0"
                onClick={onLanguage}
              >
                <span className="flex items-center gap-2">
                  <Languages />
                  שפת ממשק
                </span>
                <span dir="ltr">{language === "he" ? "English" : "עברית"}</span>
              </Button>
              <div>
                <p className="mb-2 text-sm font-medium">{text.appearance}</p>
                <div className="grid grid-cols-3 gap-2">
                  {themes.map(({ value, label, icon: Icon }) => (
                    <Button
                      key={value}
                      variant={user.theme === value ? "secondary" : "ghost"}
                      className="h-auto min-w-0 flex-col gap-2 py-3"
                      onClick={() => onTheme(value)}
                    >
                      <Icon />
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid gap-1">
                <SettingToggle
                  label={text.seconds}
                  checked={user.showSeconds}
                  onChange={(value) => onToggle("showSeconds", value)}
                />
                <SettingToggle
                  label={text.gregorianDate}
                  checked={user.showGregorianDate}
                  onChange={(value) => onToggle("showGregorianDate", value)}
                />
                <SettingToggle
                  label={text.hebrewDate}
                  checked={user.showHebrewDate}
                  onChange={(value) => onToggle("showHebrewDate", value)}
                />
                <SettingToggle
                  label={text.weather}
                  checked={user.showWeather}
                  onChange={(value) => onToggle("showWeather", value)}
                />
                {user.showWeather && (
                  <SettingToggle
                    label={text.weatherHighLow}
                    checked={user.showWeatherHighLow}
                    onChange={(value) =>
                      onToggle("showWeatherHighLow", value)
                    }
                  />
                )}
                <SettingToggle
                  label="הצגת נגן Spotify במסך הראשי"
                  checked={user.showSpotifyPlayer}
                  onChange={(value) => onToggle("showSpotifyPlayer", value)}
                />
                <SettingToggle
                  label="הצגת ברכה אישית"
                  checked={user.showGreeting}
                  onChange={(value) => onToggle("showGreeting", value)}
                />
                <SettingToggle
                  label={text.showCalendarAgenda}
                  checked={user.showCalendarAgenda}
                  onChange={(value) => onToggle("showCalendarAgenda", value)}
                />
                <SettingToggle
                  label={
                    language === "he"
                      ? "הצגת מד ריכוז מבוסס מצלמה"
                      : "Show camera attention meter"
                  }
                  checked={user.showAttentionCamera}
                  onChange={(value) => onToggle("showAttentionCamera", value)}
                />
                {user.showAttentionCamera && (
                  <>
                    <SettingToggle
                      label={
                        language === "he"
                          ? "סנכרון סיכומי זמן ריכוז בין המכשירים"
                          : "Sync attention time summaries across devices"
                      }
                      checked={user.attentionSyncEnabled}
                      onChange={(value) =>
                        onToggle("attentionSyncEnabled", value)
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-start text-destructive"
                      onClick={() => void onDeleteAttentionData()}
                    >
                      <Trash2 />
                      {language === "he"
                        ? "מחיקת סיכומי זמן הריכוז"
                        : "Delete attention summaries"}
                    </Button>
                  </>
                )}
              </div>
              <section className="grid gap-3 border-b border-border/70 pb-5">
                <p className="text-sm font-medium">{text.countdown}</p>
                <select
                  className="minimal-field h-10 min-w-0 text-sm"
                  value={clockMode}
                  onChange={(event) =>
                    setClockMode(event.target.value as CountdownMode)
                  }
                  aria-label={text.countdown}
                >
                  <option value="NEXT_EVENT">{text.nextCalendarEvent}</option>
                  <option value="END_OF_DAY">{text.endOfDay}</option>
                  <option value="CUSTOM_TIME">{text.customTime}</option>
                  <option value="TIMER">{text.timer}</option>
                </select>
                {clockMode === "CUSTOM_TIME" && (
                  <input
                    className="minimal-field h-10 min-w-0 text-sm"
                    type="time"
                    value={clockTime}
                    onChange={(event) => setClockTime(event.target.value)}
                    aria-label={text.customTime}
                  />
                )}
                {clockMode === "TIMER" && (
                  <div className="flex items-center gap-2">
                    <input
                      className="minimal-field h-10 min-w-0 flex-1 text-sm"
                      type="number"
                      min={1}
                      max={1440}
                      value={clockDuration}
                      onChange={(event) => setClockDuration(event.target.value)}
                      aria-label={text.timer}
                    />
                    <span className="text-sm text-muted-foreground">
                      {text.minutes}
                    </span>
                  </div>
                )}
                <SettingToggle
                  label={text.tickSound}
                  checked={user.countdownTicking}
                  onChange={onTicking}
                />
                <SettingToggle
                  label={text.countdownInWords}
                  checked={user.countdownInWords}
                  onChange={(value) => onToggle("countdownInWords", value)}
                />
                <Button
                  size="sm"
                  type="button"
                  className="justify-self-end"
                  onClick={() => {
                    const countdownDurationMinutes = Math.max(
                      1,
                      Number(clockDuration) || 1,
                    );
                    onCountdown({
                      countdownMode: clockMode,
                      countdownTargetTime:
                        clockMode === "CUSTOM_TIME" ? clockTime || null : null,
                      countdownDurationMinutes,
                      countdownEndsAt:
                        clockMode === "TIMER"
                          ? new Date(
                              Date.now() + countdownDurationMinutes * 60_000,
                            ).toISOString()
                          : null,
                    });
                  }}
                  disabled={clockMode === "CUSTOM_TIME" && !clockTime}
                >
                  {clockMode === "TIMER" ? text.startTimer : text.save}
                </Button>
              </section>
              <label
                className="grid gap-2 text-sm"
                htmlFor="default-task-estimate"
              >
                {text.defaultTaskEstimate}
                <div className="flex items-center gap-2">
                  <input
                    id="default-task-estimate"
                    className="minimal-field h-10 min-w-0 flex-1"
                    type="number"
                    min={1}
                    max={1440}
                    value={estimate}
                    onChange={(event) => setEstimate(event.target.value)}
                  />
                  <span className="text-muted-foreground">{text.minutes}</span>
                  <Button
                    size="sm"
                    onClick={() =>
                      onDefaultEstimate(Math.max(1, Number(estimate) || 1))
                    }
                  >
                    {text.save}
                  </Button>
                </div>
              </label>
            </section>
          )}
          {view === "calendar" && (
            <section>
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{text.calendarSettings}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {text.calendarSettingsCopy}
                  </p>
                </div>
                {calendars?.connected && (
                  <Badge variant="secondary">{text.connected}</Badge>
                )}
              </div>
              {!calendars ? (
                <p className="text-sm text-muted-foreground">{text.loading}</p>
              ) : !calendars.connected ? (
                <Button render={<a href="/api/calendar/google" />}>
                  {text.connectCalendar}
                  <LogIn />
                </Button>
              ) : !calendars.calendars.length ? (
                <p className="text-sm text-muted-foreground">
                  {text.noCalendars}
                </p>
              ) : (
                <div className="divide-y divide-border/70">
                  {calendars.calendars.map((calendar, index) => (
                    <label
                      key={calendar.id}
                      className="flex min-w-0 cursor-pointer items-center gap-3 py-3 text-sm"
                    >
                      <Checkbox
                        checked={calendar.selected}
                        onCheckedChange={() => onCalendar(calendar)}
                      />
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor: calendarColor(calendar, index),
                        }}
                      />
                      <span className="min-w-0 flex-1 break-words">
                        {calendar.summary}
                      </span>
                      {calendar.selected && (
                        <Check className="size-4 shrink-0 text-primary" />
                      )}
                    </label>
                  ))}
                </div>
              )}
              {calendars?.hiddenEvents.length ? (
                <section className="mt-6 border-t border-border/70 pt-5">
                  <p className="font-medium">{text.hiddenEvents}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    אירועים אלה לא מוצגים בלוח ולא נכללים בספירה לאחור.
                  </p>
                  <div className="mt-3 divide-y divide-border/70">
                    {calendars.hiddenEvents.map((event, index) => (
                      <div
                        key={event.id}
                        className="flex min-w-0 items-center gap-3 py-3 text-sm"
                      >
                        <span
                          className="size-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor: calendarColor(
                              event.calendar,
                              index,
                            ),
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate">{event.title}</p>
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {event.calendar.summary}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRestoreHiddenEvent(event.id)}
                        >
                          {text.restore}
                        </Button>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
              {calendars?.connected && (
                <section className="mt-6 border-t border-border/70 pt-5">
                  <p className="font-medium">{text.travelPlanning}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    חישוב זמן נסיעה והוספת בלוק נסיעה ליומן.
                  </p>
                  <div className="mt-4 grid gap-3">
                    <label className="grid gap-1.5 text-sm">
                      {text.travelOrigin}
                      <input
                        className="minimal-field h-10 min-w-0"
                        value={travelOrigin}
                        onChange={(event) =>
                          setTravelOrigin(event.target.value)
                        }
                      />
                    </label>
                    <label className="grid gap-1.5 text-sm">
                      {text.travelMode}
                      <select
                        className="minimal-field h-10 min-w-0"
                        value={travelMode}
                        onChange={(event) =>
                          setTravelMode(
                            event.target.value as "DRIVE" | "TRANSIT",
                          )
                        }
                      >
                        <option value="DRIVE">{text.drive}</option>
                        <option value="TRANSIT">{text.transit}</option>
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-sm">
                      {text.travelLead}
                      <div className="flex items-center gap-2">
                        <input
                          className="minimal-field h-10 min-w-0 flex-1"
                          type="number"
                          min={0}
                          max={180}
                          value={travelLead}
                          onChange={(event) =>
                            setTravelLead(event.target.value)
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          {text.minutes}
                        </span>
                      </div>
                    </label>
                    <label className="grid gap-1.5 text-sm">
                      {text.travelCalendar}
                      <select
                        className="minimal-field h-10 min-w-0"
                        value={travelCalendarId}
                        onChange={(event) =>
                          setTravelCalendarId(event.target.value)
                        }
                      >
                        <option value="">יצירה אוטומטית של יומן נסיעות</option>
                        {calendars.calendars.map((calendar) => (
                          <option key={calendar.id} value={calendar.id}>
                            {calendar.summary}
                          </option>
                        ))}
                      </select>
                    </label>
                    <Button
                      size="sm"
                      className="justify-self-end"
                      onClick={() =>
                        onTravelPreferences({
                          travelOrigin:
                            travelOrigin.trim() || user.travelOrigin,
                          travelMode,
                          travelLeadMinutes: Math.max(
                            0,
                            Number(travelLead) || 0,
                          ),
                          travelCalendarId: travelCalendarId || null,
                        })
                      }
                    >
                      {text.save}
                    </Button>
                  </div>
                </section>
              )}
            </section>
          )}
          {view === "integrations" && (
            <SpotifyPanel spotify={spotify} onRefresh={onSpotifyRefresh} />
          )}
          {(view === "projects" || view === "time") && (
            <ManagementPanel
              view={view}
              clients={clients}
              entries={entries}
              onClientsChanged={loadClients}
            />
          )}
        </div>
        <footer className="mt-3 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/70 pt-3 text-xs text-muted-foreground">
          <span dir="ltr">AIqel Focus · v0.1.0</span>
          <a
            className="underline underline-offset-4"
            href="mailto:support@aiqel.amiteam.io?subject=AIqel%20Focus%20-%20דיווח%20תקלה"
          >
            דיווח על תקלה
          </a>
          <a
            className="underline underline-offset-4"
            href="mailto:support@aiqel.amiteam.io?subject=AIqel%20Focus%20-%20בקשת%20פיצ׳ר"
          >
            בקשת פיצ׳ר
          </a>
          <a
            className="underline underline-offset-4"
            href="/accessibility-statement.html"
          >
            {text.accessibility}
          </a>
          <a
            className="underline underline-offset-4"
            href="/privacy.html"
            target="_blank"
            rel="noreferrer"
          >
            {language === "he" ? "מדיניות פרטיות" : "Privacy policy"}
          </a>
          <button
            type="button"
            className="text-destructive underline underline-offset-4"
            onClick={() => void onDeleteAccount()}
          >
            {language === "he" ? "מחיקת החשבון" : "Delete account"}
          </button>
        </footer>
      </aside>
    </>
  );
}

function ManagementPanel({
  view,
  clients,
  entries,
  onClientsChanged,
}: {
  view: "projects" | "time";
  clients: Array<{ id: string; name: string; projects: Project[] }>;
  entries: Array<{
    id: string;
    note: string | null;
    durationSec: number;
    startedAt: string;
    project?: { name: string; client?: { name: string } | null } | null;
    task?: { title: string } | null;
  }>;
  onClientsChanged: () => Promise<void>;
}) {
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectClientId, setProjectClientId] = useState("");
  const [projectColor, setProjectColor] = useState("#006d3b");
  const [managementError, setManagementError] = useState("");
  const createClient = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!clientName.trim()) return;
    try {
      setManagementError("");
      await api("/api/clients", {
        method: "POST",
        body: JSON.stringify({ name: clientName.trim() }),
      });
      setClientName("");
      await onClientsChanged();
    } catch (reason) {
      setManagementError((reason as Error).message);
    }
  };
  const createProject = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectName.trim() || !projectClientId) return;
    try {
      setManagementError("");
      await api("/api/projects", {
        method: "POST",
        body: JSON.stringify({
          name: projectName.trim(),
          clientId: projectClientId,
          color: projectColor,
        }),
      });
      setProjectName("");
      await onClientsChanged();
    } catch (reason) {
      setManagementError((reason as Error).message);
    }
  };
  if (view === "projects")
    return (
      <section className="space-y-6">
        <div>
          <p className="font-medium">לקוחות ופרויקטים</p>
          <p className="mt-1 text-sm text-muted-foreground">
            הוספה וניהול ישירות מתוך Aiqel Focus
          </p>
        </div>
        <form className="grid gap-2" onSubmit={createClient}>
          <label className="text-sm font-medium" htmlFor="client-name">
            לקוח חדש
          </label>
          <div className="flex items-center gap-2">
            <input
              id="client-name"
              className="minimal-field h-10 min-w-0 flex-1 text-sm"
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
              placeholder="שם הלקוח"
            />
            <Button size="sm" type="submit" disabled={!clientName.trim()}>
              <Plus />
              הוספה
            </Button>
          </div>
        </form>
        <form className="grid gap-2" onSubmit={createProject}>
          <label className="text-sm font-medium" htmlFor="project-name">
            פרויקט חדש
          </label>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]">
            <input
              id="project-name"
              className="minimal-field h-10 min-w-0 text-sm"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder="שם הפרויקט"
            />
            <select
              className="minimal-field h-10 min-w-0 text-sm"
              value={projectClientId}
              onChange={(event) => setProjectClientId(event.target.value)}
              aria-label="שיוך ללקוח"
            >
              <option value="">בחירת לקוח</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <input
              className="h-10 w-11 cursor-pointer rounded-md border border-border bg-transparent p-1"
              type="color"
              value={projectColor}
              onChange={(event) => setProjectColor(event.target.value)}
              aria-label="צבע הפרויקט"
            />
            <Button
              size="sm"
              type="submit"
              disabled={!projectName.trim() || !projectClientId}
            >
              <Plus />
              הוספה
            </Button>
          </div>
        </form>
        {managementError && (
          <p className="text-sm text-destructive">{managementError}</p>
        )}
        <div className="divide-y divide-border/70">
          {clients.map((client) => (
            <div key={client.id} className="py-3">
              <p className="font-medium">{client.name}</p>
              {client.projects.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {client.projects.map((project) => (
                    <span
                      key={project.id}
                      className="inline-flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <span
                        className="size-2 rounded-full"
                        style={{ backgroundColor: project.color ?? "#006d3b" }}
                      />
                      {project.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">
                  ללא פרויקטים
                </p>
              )}
            </div>
          ))}
          {!clients.length && (
            <p className="py-3 text-sm text-muted-foreground">
              הוסף/י לקוח ראשון כדי לשייך אליו פרויקטים.
            </p>
          )}
        </div>
      </section>
    );
  const exportCsv = () => {
    const body = [
      "date,description,project,duration_seconds",
      ...entries.map((entry) =>
        [
          entry.startedAt,
          entry.note ?? entry.task?.title ?? "",
          entry.project?.name ?? "",
          entry.durationSec,
        ]
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(
      new Blob([body], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "aiqel-time-entries.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section className="space-y-3">
      <Button variant="ghost" size="sm" onClick={exportCsv}>
        ייצוא CSV
      </Button>
      <div className="divide-y divide-border/70">
        {entries.map((entry) => (
          <div key={entry.id} className="py-3 text-sm">
            <p className="font-medium">
              {entry.note ?? entry.task?.title ?? "עבודה חופשית"}
            </p>
            <p className="mt-1 flex justify-between text-xs text-muted-foreground">
              <span>{entry.project?.name ?? "ללא פרויקט"}</span>
              <span dir="ltr">{formatDuration(entry.durationSec * 1000)}</span>
            </p>
          </div>
        ))}
        {!entries.length && (
          <p className="py-3 text-sm text-muted-foreground">
            אין עדיין רשומות זמן.
          </p>
        )}
      </div>
    </section>
  );
}

function TravelDialog({
  event,
  plan,
  loading,
  text,
  onMode,
  onAdd,
  onClose,
}: {
  event: CalendarEvent;
  plan: TravelPlan | null;
  loading: boolean;
  text: Copy;
  onMode: (mode: "DRIVE" | "TRANSIT") => void;
  onAdd: () => void;
  onClose: () => void;
}) {
  const duration = plan ? formatDuration(plan.durationSeconds * 1000) : "—";
  const distance = plan?.distanceMeters
    ? `${(plan.distanceMeters / 1000).toFixed(1)} ק״מ`
    : null;
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default bg-black/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-label="סגירת תכנון נסיעה"
      />
      <section
        className="fixed inset-x-4 inset-y-auto bottom-[max(1rem,env(safe-area-inset-bottom))] z-50 mx-auto w-auto max-w-md rounded-2xl bg-popover p-5 text-popover-foreground shadow-2xl sm:inset-x-auto sm:start-8 sm:bottom-8"
        role="dialog"
        aria-modal="true"
        aria-label={text.travelPlanning}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">
              {text.travelPlanning}
            </p>
            <h2 className="mt-1 break-words text-lg font-semibold">
              {event.title}
            </h2>
            <p className="mt-1 break-words text-sm text-muted-foreground">
              {event.location}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="סגירה"
          >
            <X />
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            variant={plan?.mode === "DRIVE" ? "secondary" : "ghost"}
            onClick={() => onMode("DRIVE")}
            disabled={loading}
          >
            <CarFront />
            {text.drive}
          </Button>
          <Button
            variant={plan?.mode === "TRANSIT" ? "secondary" : "ghost"}
            onClick={() => onMode("TRANSIT")}
            disabled={loading}
          >
            <Globe2 />
            {text.transit}
          </Button>
        </div>
        <div className="mt-4 rounded-xl bg-muted/70 p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">מחשבים זמן נסיעה…</p>
          ) : plan ? (
            <>
              <p className="font-mono text-2xl font-semibold" dir="ltr">
                {duration}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {distance ? `${distance} · ` : ""}יציאה מומלצת:{" "}
                <time dir="ltr" className="font-mono">
                  {formatTime(plan.recommendedDepartureAt, "he", false)}
                </time>
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">לא נמצאה דרך זמינה.</p>
          )}
        </div>
        <Button
          className="mt-4 w-full"
          onClick={onAdd}
          disabled={!plan || loading}
        >
          <CalendarDays />
          {text.addTravel}
        </Button>
      </section>
    </>
  );
}

function SpotifyMiniPlayer({
  spotify,
  onCommand,
}: {
  spotify: SpotifyState;
  onCommand: (action: "play" | "pause" | "next" | "previous") => void;
}) {
  const item = spotify.playback?.item;
  const device =
    spotify.playback?.device?.name ??
    spotify.devices.find((entry) => entry.is_active)?.name;
  const needsReconnect = spotify.status === "RECONNECT_REQUIRED";
  return (
    <div className="flex shrink-0 items-center gap-2 rounded-xl bg-muted/70 p-1.5 text-start sm:max-w-[26rem] sm:px-2 sm:py-1.5">
      <div className="relative size-10 shrink-0 overflow-hidden rounded-md">
        {item?.album?.images?.[0]?.url ? (
          <img
            className="size-full object-cover"
            src={item.album.images[0].url}
            alt=""
          />
        ) : (
          <div className="size-full bg-primary/10" />
        )}
        <Button
          variant="ghost"
          size="icon"
          className="absolute inset-0 size-full rounded-md bg-black/25 text-white hover:bg-black/40 sm:hidden"
          onClick={() =>
            onCommand(spotify.playback?.is_playing ? "pause" : "play")
          }
          aria-label={spotify.playback?.is_playing ? "השהה" : "נגן"}
        >
          {spotify.playback?.is_playing ? (
            <Pause className="size-4" />
          ) : (
            <Play className="size-4" />
          )}
        </Button>
      </div>
      <div className="hidden min-w-0 flex-1 leading-tight sm:block">
        <p className="truncate text-xs font-semibold">
          {needsReconnect
            ? "נדרש חיבור מחדש ל-Spotify"
            : item?.name ?? "לא מתנגן כרגע"}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
          {needsReconnect
            ? "יש לאשר מחדש את הרשאות הנגן"
            : item?.artists?.map((artist) => artist.name).join(", ") ??
              "Spotify"}
          {device ? ` · ${device}` : ""}
        </p>
      </div>
      <div className="hidden shrink-0 items-center gap-0.5 sm:flex" dir="ltr">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={() => onCommand("previous")}
          aria-label="שיר קודם"
        >
          <SkipBack className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={() =>
            onCommand(spotify.playback?.is_playing ? "pause" : "play")
          }
          aria-label={spotify.playback?.is_playing ? "השהה" : "נגן"}
        >
          {spotify.playback?.is_playing ? (
            <Pause className="size-3.5" />
          ) : (
            <Play className="size-3.5" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={() => onCommand("next")}
          aria-label="שיר הבא"
        >
          <SkipForward className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

function SpotifyPanel({
  spotify,
  onRefresh,
}: {
  spotify: SpotifyState | null;
  onRefresh: () => Promise<void>;
}) {
  const disconnect = async () => {
    await api("/api/spotify", { method: "DELETE" });
    await onRefresh();
  };
  return (
    <section className="grid gap-5">
      <div>
        <p className="font-medium">חיבורים</p>
        <p className="mt-1 text-sm text-muted-foreground">
          ניהול חיבורי שירותים לחשבון שלך
        </p>
      </div>
      <div className="flex items-center justify-between gap-4 rounded-2xl bg-muted/55 p-4">
        <div className="min-w-0">
          <p className="font-medium">Spotify</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {spotify?.status === "RECONNECT_REQUIRED"
              ? "נדרש חיבור מחדש כדי לקרוא את הנגן הפעיל"
              : spotify?.connected
                ? spotify.status === "NO_ACTIVE_PLAYBACK"
                  ? "מחובר — אין נגינה פעילה ש-Spotify מדווח עליה"
                  : "מחובר לחשבון שלך"
                : "לא מחובר"}
          </p>
        </div>
        {spotify?.configured ? (
          spotify.connected ? (
            <div className="flex items-center gap-2">
              {spotify.status === "RECONNECT_REQUIRED" && (
                <Button size="sm" render={<a href="/api/spotify/connect" />}>
                  חיבור מחדש
                  <LogIn />
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={disconnect}>
                ניתוק
              </Button>
            </div>
          ) : (
            <Button size="sm" render={<a href="/api/spotify/connect" />}>
              חיבור
              <LogIn />
            </Button>
          )
        ) : (
          <span className="text-sm text-muted-foreground">לא זמין</span>
        )}
      </div>
    </section>
  );
}
function SettingToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 border-b border-border/70 py-3 text-sm last:border-0">
      <span className="min-w-0 break-words">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
function Login({
  language,
  text,
  setLanguage,
  email,
  setEmail,
  sent,
  error,
  onSubmit,
}: {
  language: Language;
  text: Copy;
  setLanguage: (value: Language) => void;
  email: string;
  setEmail: (value: string) => void;
  sent: boolean;
  error: string;
  onSubmit: (event: React.FormEvent) => void;
}) {
  return (
    <main className="grid min-h-svh place-items-center bg-muted/30 p-4 sm:p-5">
      <Button
        className="absolute top-4 end-4 sm:top-5"
        variant="ghost"
        onClick={() => setLanguage(language === "he" ? "en" : "he")}
      >
        <Globe2 />
        {language === "he" ? "English" : "עברית"}
      </Button>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="brand mb-9">
            <span className="brand-mark">A</span>
            <span>AIqel</span>
            <strong>Focus</strong>
          </div>
          <p className="text-xs font-medium tracking-[0.16em] text-muted-foreground uppercase">
            DARE TO KNOW
          </p>
          <CardTitle className="text-3xl sm:text-4xl">
            {text.loginTitle}
          </CardTitle>
          <CardDescription className="text-base">
            {text.loginCopy}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            className="w-full"
            size="lg"
            render={
              isNative ? (
                <button
                  type="button"
                  onClick={() => void openNativeGoogleLogin()}
                />
              ) : (
                <a href="/api/auth/google" />
              )
            }
          >
            <LogIn />
            {text.google}
          </Button>
          {isNative && (
            <Button
              className="mt-3 w-full"
              size="lg"
              variant="outline"
              onClick={() =>
                void openNativeAppleLogin().catch((reason: Error) => {
                  if (!reason.message.includes("cancel"))
                    window.alert(reason.message);
                })
              }
            >
              <Apple />
              {text.apple}
            </Button>
          )}
          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
            {text.magic}
          </div>
          {sent ? (
            <p
              className="text-sm text-emerald-600 dark:text-emerald-400"
              role="status"
            >
              {language === "he"
                ? "אם הכתובת מורשית, קישור בטוח בדרך אליך."
                : "If your address is allowed, a secure link is on its way."}
            </p>
          ) : (
            <form className="grid gap-3" onSubmit={onSubmit}>
              <label className="grid gap-2 text-sm" htmlFor="email">
                {text.email}
                <input
                  className="h-10 rounded-lg border border-input bg-background px-3"
                  id="email"
                  type="email"
                  dir="ltr"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  placeholder="you@example.com"
                />
              </label>
              <Button type="submit">{text.send}</Button>
            </form>
          )}
          {error && (
            <p className="mt-4 text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <p className="mt-6 text-center text-xs text-muted-foreground">
            {text.invite}
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
